/* eslint-disable no-unused-vars */
import { string } from 'prop-types';
import React, { useState } from 'react';
import { Button, Spinner } from 'ui-component-library';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import InfoSection from '../../card-shared-components/card-info-section/card-info-section';
import CommentBox from '../../comments-box/comments-box';
import { BottomRow, LoadingContainer } from './activity-comment-form.style';
import { commentOnActivity, getLoadDetails } from '../../../../../utils/api/load';
import { showErrorMessage, showSuccessMessage, validate } from '../../../../../utils/common';
import { fetchLoadDetails } from '../../../../../store/load-details/thunkActions';
import { updateLoadDetails } from '../../../../../store/load-details/slice';
import { generateCommentOnEventOptimisticResponse } from '../../../../../utils/optimistic-responses';
import { COMMENTS_CHARACTER_LIMIT } from '../../../../../constants';

export const schema = yup.object().shape({
  comment: yup.string().required().max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less than ${COMMENTS_CHARACTER_LIMIT}`)
    .min(1, 'Comments cannot be empty'),
});

/**
 * Form to submit a comment on a particular event on a load
 * @param {object} params - Object that contains props for this component
 * @param {string} params.eventId - ID of the event that is being commented on
 * @param {string} params.loadId - ID of the load that the event exists on
 * @returns {element} - The Activity comment form component
 */
const ActivityCommentForm = ({ eventId, loadId }) => {
  const [comment, setComment] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadDetails = useSelector((state) => state.loadDetails.data);
  const user = useSelector((store) => store.oidc.user);

  const dispatch = useDispatch();

  /**
   * Checks if the form values are valid
   * @return {boolean}
   */
  const checkValid = () => {
    const payload = {
      comment,
    };
    const valid = validate(payload, schema);
    return valid;
  };

  /**
   * Handles when the comment state has been changed
   * @param {object} event - DOM event from a form change
   */
  const onCommentChanged = (event) => {
    setComment(event.target.value);
  };

  /**
   * Handles the submission of the comment form
   * @returns {void}
   */
  const handleSubmit = () => {
    const payload = {
      text: comment,
      eventId,
      loadId,
    };
    setLoading(true);
    commentOnActivity(payload).then((res) => {
      showSuccessMessage(`Comment successfully added on Load #${loadId}.`);
      // Optimistically show the new comment immediatly
      const optimisticLoadDetailsResponse = generateCommentOnEventOptimisticResponse(loadDetails, payload, user);
      setComment(null);
      dispatch(updateLoadDetails(optimisticLoadDetailsResponse));
      dispatch(fetchLoadDetails(loadId));
    }).catch((error) => {
      showErrorMessage(`Something went wrong. Unable to comment on Load #${loadId}`);
    }).finally(() => {
      setLoading(false);
    });
  };

  // If loading show a spinner
  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  const valid = checkValid();
  return (
    <InfoSection testId="activity-comment-form">
      <CommentBox
        label="Add Comment"
        placeholder="Add comments about this problem"
        comment={comment}
        onCommentChanged={onCommentChanged}
        rows={3}
        showLabel={false}
        required
      />
      <BottomRow>
        <Button size="small" onClick={() => setComment(null)} text="Clear" />
        <Button size="small" onClick={handleSubmit} text="Save" isDisabled={!valid} />
      </BottomRow>
    </InfoSection>
  );
};

ActivityCommentForm.propTypes = {
  eventId: string.isRequired,
  loadId: string.isRequired,
};

export default ActivityCommentForm;
